import { action, computed, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import { ServiceItemPresenceEditorViewModel } from '@/features/legends/data-sources/sources/ServiceItemPresenceEditorViewModel'
import { UserWorkRequestCountEditorViewModel } from '@/features/legends/data-sources/sources/UserWorkRequestCountEditorViewModel'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { manageEach } from '@/features/legends/editor/collections'
import { UnknownElementError } from '@/features/legends/editor/legend-document'
import { validateElements } from '@/utils/validx-util'
import type {
  KnownRoutingContextSource,
  RoutingContextSource,
} from './data-sources'
import {
  type RoutingContextSourceType,
  routingContextSourceTypes,
} from './routing-context-source-types'

/**
 * Any routing data source editor, narrowed by its `type`.
 */
export type AnyRoutingContextSourceEditor =
  ServiceItemPresenceEditorViewModel | UserWorkRequestCountEditorViewModel

/**
 * The legend's routing data sources. The list's own error, when any source
 * is invalid, is `validation.getError('sources')`.
 */
export class RoutingContextSourcesBuilderViewModel extends LegendPartViewModelBase {
  private readonly _schema: IValidationSchema<RoutingContextSourcesBuilderViewModel> =
    {
      sources: [validateElements('There are invalid routing sources')],
    }

  readonly sources = observable.array<AnyRoutingContextSourceEditor>([], {
    deep: false,
  })

  readonly validation = validationContext(this, this._schema)

  constructor(dependencies: LegendPartDependencies) {
    super(dependencies)
    this.lifecycle.manage(manageEach(this.lifecycle, () => this.sources))
  }

  /**
   * The sources, as entities routing filters can reference.
   */
  @computed
  get entityDescriptors() {
    return this.sources.flatMap((s) => s.entityDescriptors)
  }

  /**
   * A legend can only have one work request count.
   */
  @computed
  get canAddUserWorkRequestCount() {
    return !this.sources.some((s) => s.type === 'UserWorkRequestCount')
  }

  /**
   * The source types that can be added.
   */
  @computed
  get availableSourceTypes() {
    return [
      ...(this.canAddUserWorkRequestCount
        ? [routingContextSourceTypes.UserWorkRequestCount]
        : []),
      routingContextSourceTypes.ServiceItemPresence,
    ]
  }

  /**
   * Adds a source of the type, with its default ID.
   */
  @action.bound
  addSource(type: RoutingContextSourceType) {
    this.sources.push(this._createSource(type))
  }

  /**
   * Removes the source.
   */
  @action.bound
  remove(source: AnyRoutingContextSourceEditor) {
    this.sources.remove(source)
  }

  /**
   * Sets the sources from the legend document.
   */
  @action
  hydrate(sources: RoutingContextSource[] | undefined) {
    this.sources.replace(
      (sources ?? []).map((source) => {
        if (source.type === 'Unknown') {
          throw new UnknownElementError('routing data source', source)
        }
        const sourceVm = this._createSource(source.type)
        // SAFETY: the editor was created for the source's own type.
        ;(sourceVm as { hydrate(source: RoutingContextSource): void }).hydrate(
          source,
        )
        return sourceVm
      }),
    )
  }

  /**
   * Shows the analyzer's errors on the sources.
   */
  receiveErrors(descriptor: ErrorDescriptor | null | undefined) {
    receiveErrors(this.sources, descriptor?.inner)
  }

  /**
   * The sources as the legend document stores them.
   */
  toJSON(): KnownRoutingContextSource[] {
    return this.sources.map((s) => s.toJSON())
  }

  private _createSource(
    type: RoutingContextSourceType,
  ): AnyRoutingContextSourceEditor {
    switch (type) {
      case 'ServiceItemPresence':
        return new ServiceItemPresenceEditorViewModel(this.dependencies)
      case 'UserWorkRequestCount':
        return new UserWorkRequestCountEditorViewModel(this.dependencies)
    }
  }
}
